import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { Spinner, ButtonGroup, Button as BSButton } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFireAlt, faCoin, faTruck } from "@fortawesome/pro-solid-svg-icons";
import { useTheme } from "styled-components";
import { getProdutos } from "../../../api/produtos";
import ProdutoVertical from "../../../components/ProdutoVertical";
import BadgeEsgotado from "../../../components/BadgeEsgotado";
import SmartImage from "../../../components/SmartImage";
import * as S from "./styles";
import { SearchContext } from "../..";
import styled from "styled-components";

// estoque abaixo da quantidade minima de compra = esgotado
const minQty = (p) => parseInt(p.quantidade_minima, 10) || 1;
// 0 = disponivel, 1 = pre-venda (avise-me), 2 = esgotado
const grupoDisponibilidade = (p) => {
  if (p.status === "pre_lancamento") return 1;
  if ((parseFloat(p.stock) || 0) < minQty(p)) return 2;
  return 0;
};

function Listagem({ readOnly = false }) {
  window.document.title = "Organnact - Listagem de Produtos";
  const theme = useTheme();
  const { needle } = useContext(SearchContext);
  const navigate = useNavigate();
  const auth = useSelector((state) => state.authReducer) || {};
  const { user } = auth;
  const roles = auth.roles?.length ? auth.roles : user?.roles || [];
  const isAdmin = roles.includes("admin");
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [destaques, setDestaques] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10000);
  const [hasMore, setHasMore] = useState(true);
  // so o admin escolhe a divisao; gestor/distribuidor sao filtrados pelo backend
  const [tipoFilter, setTipoFilter] = useState("pet");
  const [sortBy, setSortBy] = useState("relevancia");

  useEffect(() => {
    loadProdutos();
  }, [page, perPage, needle, tipoFilter]);

  const produtosOrdenados = useMemo(() => {
    const arr = [...produtos];
    const porNome = (a, b) =>
      (a.nome || "").localeCompare(b.nome || "", "pt-BR");
    // preço oculto: pré-venda ou esgotado. Não revela a faixa de preço.
    const precoOculto = (p) => grupoDisponibilidade(p) !== 0;

    switch (sortBy) {
      case "preco_asc":
      case "preco_desc": {
        const visiveis = arr.filter((p) => !precoOculto(p));
        const ocultos = arr.filter((p) => precoOculto(p));
        visiveis.sort((a, b) =>
          sortBy === "preco_asc"
            ? (parseFloat(a.preco) || 0) - (parseFloat(b.preco) || 0)
            : (parseFloat(b.preco) || 0) - (parseFloat(a.preco) || 0)
        );
        ocultos.sort(porNome); // ocultos sempre por último, em ordem alfabética
        return [...visiveis, ...ocultos];
      }
      case "nome_asc":
        return arr.sort(porNome);
      case "nome_desc":
        return arr.sort((a, b) => porNome(b, a));
      case "relevancia":
      default:
        // disponiveis primeiro, depois pre-venda (avise-me), esgotados por ultimo
        return arr.sort(
          (a, b) =>
            grupoDisponibilidade(a) - grupoDisponibilidade(b) ||
            (b.destaque ? 1 : 0) - (a.destaque ? 1 : 0) ||
            porNome(a, b)
        );
    }
  }, [produtos, sortBy]);

  async function loadProdutos() {
    // vitrine readOnly usa o MESMO endpoint do distribuidor (/products/all);
    // apenas admin manda o tipo do toggle — o backend forca o tipo dos demais
    const tipo = readOnly && isAdmin ? tipoFilter : null;
    let nprodutos = await getProdutos(page, perPage, needle, false, tipo);
    // destaques escondem esgotados, mas mantem pre-venda (divulgacao)
    let ndestaques = nprodutos.filter(
      (produto) => produto.destaque && grupoDisponibilidade(produto) !== 2
    );
    setProdutos(nprodutos);
    setDestaques(ndestaques);
    setLoading(false);
    if (nprodutos.length < perPage) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }
  }

  function gotoProduto(id) {
    if (readOnly) {
      navigate(`/admin/vitrine/produto/${id}`);
    } else {
      navigate(`/produto/${id}`);
    }
  }

  if (loading) {
    return (
      <S.Container style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spinner />
      </S.Container>
    );
  }

  return (
    <S.Container>
      {readOnly && isAdmin && (
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: "#666" }}>Filtrar por divisao:</span>
          <ButtonGroup size="sm">
            <BSButton
              color={tipoFilter === "pet" ? "warning" : "secondary"}
              outline={tipoFilter !== "pet"}
              onClick={() => setTipoFilter("pet")}
            >
              PET
            </BSButton>
            <BSButton
              color={tipoFilter === "equi" ? "primary" : "secondary"}
              outline={tipoFilter !== "equi"}
              onClick={() => setTipoFilter("equi")}
            >
              EQUI
            </BSButton>
          </ButtonGroup>
        </div>
      )}

      {destaques.length > 0 && (
        <S.DestaquesSection>
          <S.DestaquesContainer>
            <S.DestaquesHeader>
              <S.DestaquesTitle>
                <FontAwesomeIcon icon={faFireAlt} color="#ff5900" /> Destaques
              </S.DestaquesTitle>
            </S.DestaquesHeader>
            <S.DestaquesScroll>
              {destaques.map((produto) => {
                let imagem = produto?.imagens?.[0];
                return (
                  <DestaqueCard
                    key={produto.id}
                    onClick={() => gotoProduto(produto.id)}
                  >
                    <DestaqueImage>
                      <SmartImage src={imagem} width={160} height={160} alt={produto.nome} />
                    </DestaqueImage>
                    <DestaqueNome>{produto.nome}</DestaqueNome>
                    {produto.status === "pre_lancamento" ? (
                      <BadgeEsgotado />
                    ) : (
                      <DestaquePreco>
                        <FontAwesomeIcon icon={faCoin} color={theme.primary} style={{ fontSize: 13 }} />
                        {produto.preco}
                      </DestaquePreco>
                    )}
                  </DestaqueCard>
                );
              })}
            </S.DestaquesScroll>
          </S.DestaquesContainer>
        </S.DestaquesSection>
      )}

      <S.ResultsHeader>
        <S.ResultsCount>
          <strong>{produtos.length} produtos</strong> encontrados
        </S.ResultsCount>
        <S.SortWrapper>
          <span>Ordenar por:</span>
          <S.SortSelect
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="relevancia">Mais relevantes</option>
            <option value="preco_asc">Menor preço</option>
            <option value="preco_desc">Maior preço</option>
            <option value="nome_asc">Nome: A-Z</option>
            <option value="nome_desc">Nome: Z-A</option>
          </S.SortSelect>
        </S.SortWrapper>
      </S.ResultsHeader>

      <S.ListContainer>
        {produtosOrdenados.map((produto, i) => {
          let imagem = produto?.imagens?.[0];
          return (
            <ProdutoVertical
              key={produto.id}
              id={produto.id}
              nome={produto.nome}
              foto={imagem}
              preco={produto.preco}
              status={produto.status}
              destaque={produto.destaque}
              estoque={parseFloat(produto?.stock)}
              quantidadeMinima={parseInt(produto?.quantidade_minima, 10) || 1}
              border={false}
              onClick={() => gotoProduto(produto.id)}
              readOnly={readOnly}
            />
          );
        })}
      </S.ListContainer>
    </S.Container>
  );
}

/* Destaque Card mini (carousel) */
const DestaqueCard = styled.div`
  min-width: 180px;
  max-width: 180px;
  scroll-snap-align: start;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border-radius: 6px;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const DestaqueImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  & img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const DestaqueNome = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: #333;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const DestaquePreco = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #333;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DestaqueShipping = styled.div`
  font-size: 11px;
  color: #00a650;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export default Listagem;
